/**
 * ============================================================================
 * [P1-03] DualEditor Component
 *
 * 듀얼 에디터 메인 컴포넌트
 *
 * 구조:
 * - 왼쪽: 레퍼런스 글 패널
 * - 오른쪽: 수정/작성 글 패널
 *
 * 기능:
 * - useAppStore와 바인딩된 Controlled Component
 * - debounce를 활용한 자동 저장 로직 (placeholder)
 *
 * Safety:
 * - Controlled Component 패턴으로 textarea value와 상태 동기화
 * - useCallback으로 핸들러 메모이제이션
 * - useEffect cleanup으로 메모리 누수 방지
 * ============================================================================
 */

import { useEffect, useCallback, useRef, useState } from 'react'
import { useAppStore, useEditContent, useRefContent, useCharLimit, useIsSaving } from '@/stores'
import { useAuth } from '@/hooks/useAuth'
import { debounce } from '@/utils/common'
import { createText, updateText } from '@/services/firestore/texts'
import { EditorPanel } from './EditorPanel'
import { PostingModal } from './PostingModal'
import { CharLimitToggle } from './CharLimitToggle'
import { ReferenceSelectionPanel } from './ReferenceSelectionPanel'
import { ReferenceAnalysisModal } from '@/components/reference-analysis'
import { useLLMValidation } from '@/hooks/useLLMValidation'
import type { ReferenceItem } from '@/types'
import styles from './Editor.module.css'

// ============================================================================
// 상수 정의
// ============================================================================
const AUTO_SAVE_DELAY = 1000 // 1초 디바운스

// ============================================================================
// [P1-03] DualEditor 컴포넌트
// ============================================================================
export function DualEditor() {
  // ========================================================================
  // Store 상태 및 액션
  // ========================================================================
  // selectors를 통한 상태 구독 (불필요한 리렌더링 방지)
  const editContent = useEditContent()
  const refContent = useRefContent()
  const charLimit = useCharLimit()

  // 추가 상태
  const editTopic = useAppStore((state) => state.editor.editTopic)
  const refSource = useAppStore((state) => state.editor.refSource)
  const refType = useAppStore((state) => state.editor.refType)

  // 편집 중인 글 id (null=신규 생성 / 값=기존 글 수정) — 중복 생성 버그 수정
  const editingEditId = useAppStore((state) => state.editor.editingEditId)
  const editingRefId = useAppStore((state) => state.editor.editingRefId)

  // 액션
  const setEditContent = useAppStore((state) => state.setEditContent)
  const setEditTopic = useAppStore((state) => state.setEditTopic)
  const setRefContent = useAppStore((state) => state.setRefContent)
  const setRefSource = useAppStore((state) => state.setRefSource)
  const setRefType = useAppStore((state) => state.setRefType)
  const clearEditor = useAppStore((state) => state.clearEditor)
  const setIsSaving = useAppStore((state) => state.setIsSaving)

  // ========================================================================
  // LLM 검증 Hook
  // ========================================================================
  const { validateWithLLM } = useLLMValidation()

  // ========================================================================
  // Debounce 자동 저장 로직
  // - useRef로 debounce 함수 인스턴스 유지
  // - 콘텐츠 변경 시 자동 저장 트리거
  // ========================================================================
  const debouncedSaveRef = useRef<ReturnType<typeof debounce<() => void>> | null>(null)

  // 자동 저장 함수 (UI 시뮬레이션 - 실제 저장은 Zustand persist가 담당)
  const autoSave = useCallback(() => {
    // 내용이 없으면 패스
    if (!editContent && !refContent) return

    setIsSaving(true)
    setIsTempSaved(false)

    // 저장 완료 시뮬레이션 (UX용 딜레이)
    setTimeout(() => {
      setIsSaving(false)
      setIsTempSaved(true)
    }, 600)
  }, [editContent, refContent, setIsSaving])

  // debounce 인스턴스 생성/업데이트
  useEffect(() => {
    debouncedSaveRef.current = debounce(autoSave, AUTO_SAVE_DELAY)
  }, [autoSave])

  // 콘텐츠 변경 감지 및 자동 저장 트리거
  useEffect(() => {
    // 콘텐츠가 비어있지 않을 때만 자동 저장 (undefined 방어)
    const safeEditContent = editContent ?? ''
    if (safeEditContent.trim().length > 0) {
      debouncedSaveRef.current?.()
    }
  }, [editContent])

  // ========================================================================
  // 이벤트 핸들러
  // ========================================================================
  const handleEditContentChange = useCallback((content: string) => {
    setEditContent(content)
  }, [setEditContent])

  const handleEditTopicChange = useCallback((topic: string) => {
    setEditTopic(topic)
  }, [setEditTopic])

  const handleRefContentChange = useCallback((content: string) => {
    setRefContent(content)
  }, [setRefContent])

  const handleRefSourceChange = useCallback((source: string) => {
    setRefSource(source)
  }, [setRefSource])

  const handleRefTypeChange = useCallback((type: 'structure' | 'idea' | 'unspecified') => {
    setRefType(type)
  }, [setRefType])

  const handleClearEdit = useCallback(() => {
    clearEditor('edit')
  }, [clearEditor])

  const handleClearRef = useCallback(() => {
    clearEditor('reference')
  }, [clearEditor])

  // ========================================================================
  // Copy 핸들러 (클립보드 복사)
  // ========================================================================
  const handleCopyEdit = useCallback(async () => {
    if (!editContent?.trim()) return
    try {
      await navigator.clipboard.writeText(editContent)
      alert('글이 복사되었습니다!')
    } catch (error) {
      console.error('[Copy] 복사 실패:', error)
      alert('복사 중 오류가 발생했습니다.')
    }
  }, [editContent])

  const handleCopyRef = useCallback(async () => {
    if (!refContent?.trim()) return
    try {
      await navigator.clipboard.writeText(refContent)
      alert('레퍼런스가 복사되었습니다!')
    } catch (error) {
      console.error('[Copy] 복사 실패:', error)
      alert('복사 중 오류가 발생했습니다.')
    }
  }, [refContent])

  // ========================================================================
  // 반자동 포스팅 핸들러 (Threads)
  // ========================================================================
  const handlePostEdit = useCallback(() => {
    if (!editContent?.trim()) return
    setIsPostingModalOpen(true)
  }, [editContent])

  const handleAnalyzeRefDraft = useCallback(() => {
    const content = refContent?.trim()
    if (!content) return

    setRefAnalysisSource({
      id: editingRefId ?? `draft-reference-${Date.now()}`,
      title: refSource?.trim() || '현재 레퍼런스 글',
      content,
      sourceType: 'saved',
      category: refType,
      createdAt: new Date().toISOString(),
    })
    setIsRefAnalysisOpen(true)
  }, [editingRefId, refContent, refSource, refType])

  // ========================================================================
  // 저장 핸들러 (Firestore 연동)
  // ========================================================================
  const { user } = useAuth()
  const [_isSavingEdit, setIsSavingEdit] = useState(false)
  const [_isSavingRef, setIsSavingRef] = useState(false)
  
  // 모달 상태
  const [isPostingModalOpen, setIsPostingModalOpen] = useState(false)
  const [isRefAnalysisOpen, setIsRefAnalysisOpen] = useState(false)
  const [refAnalysisSource, setRefAnalysisSource] = useState<ReferenceItem | null>(null)
  
  // 임시 저장 상태 (UI용)
  const isSaving = useIsSaving() // Store의 isSaving 사용
  const [isTempSaved, setIsTempSaved] = useState(false)

  const handleSaveEdit = useCallback(async () => {
    // 로그인 체크
    if (!user) {
      alert('로그인이 필요합니다. 먼저 로그인해주세요.')
      return
    }

    if (!editContent?.trim()) {
      alert('저장할 내용이 없습니다.')
      return
    }

    setIsSavingEdit(true)
    try {
      // 선택된 레퍼런스 ID 추출
      const selectedRefs = useAppStore.getState().reference.selectedReferences
      const linkedReferenceIds = selectedRefs.map((r) => r.id)
      const linkedReferences = linkedReferenceIds.length > 0 ? linkedReferenceIds : undefined

      if (editingEditId) {
        // 기존 글 수정 (update) — 중복 생성 방지 (id 추적)
        await updateText(user.uid, editingEditId, {
          content: editContent,
          title: editTopic || '제목 없음',
          topic: editTopic,
          linkedReferences,
        })
        console.log('[수정/작성 글] 수정(update) 완료:', editingEditId)
      } else {
        // 신규 작성 (create)
        await createText(user.uid, {
          content: editContent,
          title: editTopic || '제목 없음',
          topic: editTopic,
          charCount: editContent.length,
          type: 'edit',
          userId: user.uid,
          linkedReferences,
        })
        console.log('[수정/작성 글] 신규 저장(create) 완료')
      }
      clearEditor('edit')
      // 저장 성공 후 선택된 레퍼런스 초기화
      useAppStore.getState().clearSelectedReferences()
      alert(editingEditId ? '글이 수정되었습니다!' : '글이 저장되었습니다!')
    } catch (error) {
      console.error('[수정/작성 글] 저장 실패:', error)
      alert('저장 중 오류가 발생했습니다.')
    } finally {
      setIsSavingEdit(false)
    }
  }, [user, editContent, editTopic, clearEditor, editingEditId])

  const handleSaveRef = useCallback(async () => {
    // 로그인 체크
    if (!user) {
      alert('로그인이 필요합니다. 먼저 로그인해주세요.')
      return
    }

    if (!refContent?.trim()) {
      alert('저장할 내용이 없습니다.')
      return
    }

    setIsSavingRef(true)
    try {
      if (editingRefId) {
        // 기존 레퍼런스 수정 (update) — 중복 생성 방지 (id 추적)
        await updateText(user.uid, editingRefId, {
          content: refContent,
          title: refSource || '레퍼런스',
          topic: refSource,
          referenceType: refType,
        })
        console.log('[레퍼런스 글] 수정(update) 완료:', editingRefId)
      } else {
        await createText(user.uid, {
          content: refContent,
          title: refSource || '레퍼런스',
          topic: refSource,
          charCount: refContent.length,
          type: 'reference',
          userId: user.uid,
          referenceType: refType,
        })
        console.log('[레퍼런스 글] 신규 저장(create) 완료')
      }
      clearEditor('reference')
      alert(editingRefId ? '레퍼런스가 수정되었습니다!' : '레퍼런스가 저장되었습니다!')
    } catch (error) {
      console.error('[레퍼런스 글] 저장 실패:', error)
      alert('저장 중 오류가 발생했습니다.')
    } finally {
      setIsSavingRef(false)
    }
  }, [user, refContent, refSource, refType, clearEditor, editingRefId])

  // ========================================================================
  // 렌더링
  // ========================================================================
  return (
    <>
      {/* 글자 제한 토글 */}
      <CharLimitToggle />
      
      <div className={styles.dualEditor}>
        {/* ================================================================
            레퍼런스 글 패널 (왼쪽)
            ================================================================ */}
      <EditorPanel
        type="reference"
        title="레퍼런스 글"
        icon="📖"
        content={refContent}
        metaLabel="출처"
        metaValue={refSource}
        metaPlaceholder="출처를 입력하세요 (선택사항)"
        charLimit={charLimit}
        placeholder="참고할 글을 붙여넣거나 입력하세요..."
        onContentChange={handleRefContentChange}
        onMetaChange={handleRefSourceChange}
        onClear={handleClearRef}
        onSave={handleSaveRef}
        onCopy={handleCopyRef}
        onLLMValidate={validateWithLLM}
        onAnalyzeReference={handleAnalyzeRefDraft}
        refType={refType}
        onRefTypeChange={handleRefTypeChange}
      />

      {/* ================================================================
          수정/작성 글 패널 (오른쪽) + 레퍼런스 선택
          ================================================================ */}
      <div className={styles.editColumn}>
        <EditorPanel
          type="edit"
          title="수정/작성 글"
          icon="✏️"
          content={editContent}
          metaLabel="주제"
          metaValue={editTopic}
          metaPlaceholder="주제를 입력하세요 (선택사항)"
          charLimit={charLimit}
          placeholder="500자 글을 작성하세요..."
          onContentChange={handleEditContentChange}
          onMetaChange={handleEditTopicChange}
          onClear={handleClearEdit}
          onSave={handleSaveEdit}
          onCopy={handleCopyEdit}
          onPost={handlePostEdit}
          onLLMValidate={validateWithLLM}
        />

        {/* 참고 레퍼런스 선택 패널 */}
        <ReferenceSelectionPanel />
      </div>

      {/* 포스팅 최적화 모달 */}
      {/* 포스팅 최적화 모달 */}
      <PostingModal
        isOpen={isPostingModalOpen}
        onClose={() => setIsPostingModalOpen(false)}
        content={editContent}
      />

      <ReferenceAnalysisModal
        isOpen={isRefAnalysisOpen}
        source={refAnalysisSource}
        onClose={() => {
          setIsRefAnalysisOpen(false)
          setRefAnalysisSource(null)
        }}
      />

      {/* 임시 저장 상태 표시기 */}
      <div className={`${styles.tempSaveStatus} ${isTempSaved || isSaving ? styles.show : styles.hide}`}>
        <span className={styles.tempSaveIndicator}>
          {isSaving ? '⏳' : '💾'}
        </span>
        <span className={styles.tempSaveText}>
          {isSaving ? '저장 중...' : '임시 저장됨'}
        </span>
      </div>
    </div>
    </>
  )
}

// ============================================================================
// 기본 export
// ============================================================================
export default DualEditor
